import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "disabled"],
  _excluded2 = ["open"];
import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import H7 from 'UIComponents/elements/headings/H7';
import HR from 'UIComponents/elements/HR';
import UILink from 'UIComponents/link/UILink';
import UIList from 'UIComponents/list/UIList';
import { SkeletonView } from 'conversations-skeleton-state/components/SkeletonView';
import { mediaQueries } from 'developer-docs-shared-components/utils/mediaQueries';
import { View } from 'foundations-components/components/View';
import { BUTTON_TERTIARY_LIGHT_TEXT, CALYPSO_LIGHT, EERIE, OLAF, SLINKY } from 'HubStyleTokens/colors';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIExpandableSection from 'UIComponents/accordion/UIExpandableSection';
import { useViewport } from 'developer-docs-shared-components/hooks/useViewport';
import FormattedMessage from 'I18n/components/FormattedMessage';
import themes from 'developer-docs-shared-components/config/themes';
import ListItem from './toc/ListItem';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const expandableOpenerMobileHeight = 60;

/**
 * The opener, which wraps the `Caret` and `Title` of the
 * `UIExpandableSection`.
 */
const ExpandableOpener = styled(_ref => {
  let {
      children,
      disabled: hideCaret
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  // Exclude caret wrapper from default UIExpandableSection[Opener], using default children as a fallback for ✨component API safety✨
  let titleNode = children;
  if (hideCaret) {
    titleNode = children.find(({
      props: {
        titleText
      }
    }) => !!titleText) || children;
  }
  return (
    /*#__PURE__*/
    // @ts-expect-error defaultProps is defined
    _jsx(UIExpandableSection.defaultProps.Opener, Object.assign({}, rest, {
      children: titleNode
    }))
  );
}).withConfig({
  displayName: "Toc__ExpandableOpener",
  componentId: "sc-1ijwu6u-0"
})(["padding:0;color:", ";&&:hover{color:", ";}", ""], BUTTON_TERTIARY_LIGHT_TEXT, BUTTON_TERTIARY_LIGHT_TEXT, ({
  theme: {
    mobileBreakpoint
  }
} = themes.PUBLIC) => `
    ${mediaQueries.aboveWidth(mobileBreakpoint)} {
      color: ${SLINKY};

      /* Adjust UIClickable styles */
      cursor: text;
      user-select: auto;
    }

    ${mediaQueries.belowWidth(mobileBreakpoint)} {
      display: block;
      position: relative; /* corral the arrow/caret at mobile */
      height: ${expandableOpenerMobileHeight}px;
    }
  `);

/**
 * The arrow icons on the mobile expandable section menu.
 */
const ExpandableCaret = styled(_ref2 => {
  let {
      open
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  return /*#__PURE__*/_jsx(UIIcon, Object.assign({}, rest, {
    name: open ? 'up' : 'down',
    size: 18,
    color: EERIE
  }));
}).withConfig({
  displayName: "Toc__ExpandableCaret",
  componentId: "sc-1ijwu6u-1"
})(["", ""], ({
  theme: {
    mobileBreakpoint
  }
} = themes.PUBLIC) => `
    ${mediaQueries.belowWidth(mobileBreakpoint)} {
      position: absolute;
      right: 14px;
      top: 50%;
      transform: translateY(-50%);
    }
  `);

/**
 * Wraps the entire TOC.
 */
const ContentWrapper = styled(
// @ts-expect-error defaultProps is defined
UIExpandableSection.defaultProps.ContentWrapper).withConfig({
  displayName: "Toc__ContentWrapper",
  componentId: "sc-1ijwu6u-2"
})(["font-size:14px;padding:10px 6px 33px 6px;", ""], ({
  theme: {
    mobileBreakpoint
  }
} = themes.PUBLIC) => `
    ${mediaQueries.aboveWidth(mobileBreakpoint)} {
      padding: 0 20px 0 0;
    }
    ${mediaQueries.belowWidth(mobileBreakpoint)} {
      background-color: ${OLAF};
      border-radius: 0 0 3px 3px;
    }
  `);

/**
 * The arrow icons on the mobile expandable section menu.
 * @ts-expect-error defaultProps is defined */
const Header = styled(UIExpandableSection.defaultProps.Header).withConfig({
  displayName: "Toc__Header",
  componentId: "sc-1ijwu6u-3"
})(["border:none;padding:0;", ""], ({
  theme: {
    mobileBreakpoint
  }
} = themes.PUBLIC) => `
    ${mediaQueries.aboveWidth(mobileBreakpoint)} {
      margin: 70px 0 22px 7px;
      &::after {
        content: none;
      }
    }
    ${mediaQueries.belowWidth(mobileBreakpoint)} {
      background-color: ${CALYPSO_LIGHT};
      border-radius: 3px 3px 0 0;
      &::after {
        content: none;
      }
    }
  `);

/**
 * Title
 * @ts-expect-error defaultProps is defined */
const Title = styled(UIExpandableSection.defaultProps.Title).withConfig({
  displayName: "Toc__Title",
  componentId: "sc-1ijwu6u-4"
})(["", ""], ({
  theme: {
    mobileBreakpoint
  }
} = themes.PUBLIC) => `
    ${mediaQueries.aboveWidth(mobileBreakpoint)} {
      font-size: 16px;
    }
    ${mediaQueries.belowWidth(mobileBreakpoint)} {
      text-align: center;
      padding: 22px 32px;
    }
  `);
const DEFAULT_ACTIVE_HEADER_BUFFER_WIDTH = 150;
const getNestedTOC = (items = []) => {
  return items.reduce((acc, item) => {
    if (!item.parent) {
      // Top-level item (create a new object to avoid mutation)
      acc.push(Object.assign({}, item, {
        items: []
      }));
    } else {
      // Subitem (find the parent and add a new subitem without mutating)
      const parent = acc.find(({
        id
      }) => item.parent === id);
      if (parent) {
        parent.items = parent.items ? [...parent.items, Object.assign({}, item)] : [Object.assign({}, item)];
      }
    }
    return acc;
  }, []);
};
export default function Toc({
  activeHeaderBufferWidth = DEFAULT_ACTIVE_HEADER_BUFFER_WIDTH,
  toc,
  resources,
  containerId
}) {
  const viewportContext = useViewport();
  const {
    hash
  } = useLocation();
  const [scrolledPast, setScrolledPast] = useState([]);
  const [remaining, setRemaining] = useState([]);
  const [activeToc, setActiveToc] = useState(null);
  const [activeChildToc, setActiveChildToc] = useState(null);
  const nestedTOC = getNestedTOC(toc);
  const assignActive = scrolled => {
    if (scrolled.length > 0) {
      const id = scrolled[scrolled.length - 1].id;
      const parent = scrolled[scrolled.length - 1].parent;
      if (parent === null) {
        setActiveToc(id);
        setActiveChildToc(null);
      } else {
        setActiveToc(scrolled[scrolled.length - 1].parent);
        setActiveChildToc(id);
      }
    } else {
      setActiveToc(null);
    }
  };
  useEffect(() => {
    if (toc) {
      // if there is a hash, scroll to that item
      if (hash.length > 0) {
        const index = toc.findIndex(({
          id
        }) => id === hash.substring(1));
        const remain = toc.slice(index + 1);
        const scrolled = toc.slice(0, index + 1);
        setRemaining(remain);
        setScrolledPast(scrolled);
        assignActive(scrolled);

        // if we've already set the active item and the remaining items, no need to continue
        return;
      }

      // if there is no hash and the TOC has changed, we must be at the top of the page
      setRemaining([...toc]);
      setScrolledPast([]);
      assignActive([]);
    }
  }, [toc, activeHeaderBufferWidth, hash]);
  const onScroll = useCallback(() => {
    if (viewportContext && viewportContext.isMobile) {
      return;
    }

    /*
      Recursively look through each reamining and scrolledPast to determine if a change
      has occured.
       Most often, can look at top member elements. Worst case, will need to go all the
      way to the bottom of a stack.
    */
    const recursivelyLookUp = (scrolled, remain) => {
      if (scrolled.length > 0) {
        const el = document.getElementById(scrolled[scrolled.length - 1].id);
        if (!el) {
          scrolled.pop();
          return recursivelyLookUp(scrolled, remain);
        }
        if (el.getBoundingClientRect().y > activeHeaderBufferWidth) {
          remain.splice(0, 0, scrolled.pop());
          return recursivelyLookUp(scrolled, remain);
        } else {
          return {
            scrolled,
            remain
          };
        }
      } else {
        return {
          scrolled,
          remain
        };
      }
    };
    const recursivelyLookDown = (scrolled, remain) => {
      if (remain.length > 0) {
        const el = document.getElementById(remain[0].id);
        if (!el) {
          remain.shift();
          return recursivelyLookDown(scrolled, remain);
        }
        if (el.getBoundingClientRect().y <= activeHeaderBufferWidth) {
          scrolled.push(remain.shift());
          return recursivelyLookDown(scrolled, remain);
        } else {
          return {
            scrolled,
            remain
          };
        }
      } else {
        return {
          scrolled,
          remain
        };
      }
    };
    const {
      scrolled: scrolledAfterLookUp,
      remain: remainAfterLookUp
    } = recursivelyLookUp(scrolledPast, remaining);
    const {
      scrolled: scrolledAfterLookDown,
      remain: remainAfterLookDown
    } = recursivelyLookDown(scrolledAfterLookUp, remainAfterLookUp);
    if (scrolledAfterLookDown.length !== 0 && remainAfterLookDown.length !== 0 || remainAfterLookDown.length === 0 && scrolledAfterLookDown.length !== 0) {
      setScrolledPast(scrolledAfterLookDown);
      setRemaining(remainAfterLookDown);
      assignActive(scrolledAfterLookDown);
    }
  }, [remaining, scrolledPast, viewportContext, activeHeaderBufferWidth]);
  useEffect(() => {
    if (containerId) {
      const scrollContainer = document.getElementById(containerId);
      scrollContainer.addEventListener('scroll', onScroll, {
        passive: true
      });
      return () => scrollContainer.removeEventListener('scroll', onScroll);
    }
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, [remaining, scrolledPast, containerId, onScroll]);
  if (!toc) {
    // returns placeholder with the same height as th UIExpandableSection so that the page doesn't jump (fixes the cumulative layout shift score for mobile)
    return viewportContext.isMobile ? /*#__PURE__*/_jsx(View, {
      blockSize: `${expandableOpenerMobileHeight}px`,
      children: /*#__PURE__*/_jsx(SkeletonView, {
        blockSize: `${expandableOpenerMobileHeight}px`,
        inlineSize: "100%"
      })
    }) : /*#__PURE__*/_jsx(_Fragment, {});
  }
  return /*#__PURE__*/_jsxs(UIExpandableSection, {
    Title: Title,
    Opener: ExpandableOpener,
    disabled: !viewportContext || viewportContext && viewportContext.isDesktop,
    Caret: ExpandableCaret,
    ContentWrapper: ContentWrapper,
    Header: Header,
    indentation: "none",
    titleText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "developer-docs-ui.tableOfContentsNav.title"
    })
    // Control open state at desktop
    ,
    open: viewportContext && viewportContext.isMobile ? undefined : true,
    children: [toc && /*#__PURE__*/_jsx(View, {
      as: "ul",
      listStyleType: "none",
      "data-test-id": "table-of-contents",
      marginBlockStart: "400",
      children: nestedTOC.map(tocItem => {
        // Render child items within submenu UL
        if (tocItem.parent !== null) return null;
        return /*#__PURE__*/_jsx(ListItem, {
          activeToc: activeToc,
          activeChildToc: activeChildToc,
          item: tocItem
        }, tocItem.id);
      })
    }), resources && toc ? /*#__PURE__*/_jsx(HR, {
      distance: "small"
    }) : null, resources && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(H7, {
        className: "m-y-2",
        children: "Resources"
      }), /*#__PURE__*/_jsx(UIList, {
        childClassName: "m-top-3",
        children: resources.map(({
          href,
          text
        }) => /*#__PURE__*/_jsx(UILink, {
          external: true,
          href: href,
          children: text
        }, href))
      })]
    })]
  });
}